import { createSelector } from 'reselect';

import {
  ActionType,
  ContentType,
  ErrorTypes,
  LoadingStatusType,
} from 'interfaces';
import { FAILURE, SUCCESS } from 'services/api';
import {
  CONTENT,
  CATEGORY_CONTENTS,
  SET_CONTENT_STATUS,
  SET_CONTENT_CATEGORY_STATUS,
  INCREMENT_CONTENT_CATEGORY_PAGE,
  RESET_CONTENT_CATEGORY,
} from 'actions/content.actions';
import { sortContentBlocs } from 'core/utils/api.helpers';
import { LOADING_STATUS } from 'constants/status';

export interface ContentState {
  status: LoadingStatusType;
  currentId: string | number | null;
  currentError: ErrorTypes | null;
  byId: { [id: string]: ContentType };
  category: {
    page: number;
    status: LoadingStatusType;
    error: ErrorTypes | null;
    totalItems: number;
    contents: ContentType[];
  };
}

export const initialState: ContentState = {
  status: LOADING_STATUS.IDLE,
  currentId: null,
  currentError: null,
  byId: {},
  category: {
    page: 1,
    status: LOADING_STATUS.IDLE,
    error: null,
    totalItems: 0,
    contents: [],
  },
};

export const contentReducerSelector = (state: {
  content: ContentState;
}): ContentState => state.content;
export const currentContentSelector = createSelector(
  contentReducerSelector,
  (content) => (content.currentId ? content.byId[content.currentId] : null),
);
export const contentStatusSelector = createSelector(
  contentReducerSelector,
  (content) => content.status,
);
export const contentCategorySelector = createSelector(
  contentReducerSelector,
  (content) => content.category,
);
export const categoryContentsSelector = createSelector(
  contentCategorySelector,
  (contentCategory) => contentCategory.contents,
);
export const contentCategoriesStatusSelector = createSelector(
  contentCategorySelector,
  (contentCategory) => contentCategory.status,
);

export const contentReducer = (
  state: ContentState = initialState,
  action: ActionType,
): ContentState => {
  const { payload } = action;

  switch (action.type) {
    case SET_CONTENT_STATUS:
      return {
        ...state,
        status: payload.status,
      };
    case CONTENT[SUCCESS]:
      return {
        ...state,
        currentId: payload.id,
        byId: {
          ...state.byId,
          [payload.id]: sortContentBlocs(payload.content),
        },
      };
    case CONTENT[FAILURE]:
      return {
        ...state,
        currentId: payload.id,
        currentError: payload.error,
      };
    case SET_CONTENT_CATEGORY_STATUS:
      return {
        ...state,
        category: {
          ...state.category,
          status: payload.status,
        },
      };
    case CATEGORY_CONTENTS[SUCCESS]:
      return {
        ...state,
        category: {
          ...state.category,
          error: null,
          totalItems: payload.categoryContents['hydra:totalItems'],
          contents: [
            ...state.category.contents,
            ...payload.categoryContents['hydra:member'],
          ],
        },
      };
    case CATEGORY_CONTENTS[FAILURE]:
      return {
        ...state,
        category: {
          ...state.category,
          error: payload.error,
        },
      };
    case INCREMENT_CONTENT_CATEGORY_PAGE:
      return {
        ...state,
        category: {
          ...state.category,
          page: state.category.page + 1,
        },
      };
    case RESET_CONTENT_CATEGORY:
      return {
        ...state,
        category: {
          ...state.category,
          page: 1,
          totalItems: 0,
          contents: [],
        },
      };
    default: {
      return state;
    }
  }
};

export default contentReducer;
